import React, { useState } from 'react';
import { Button, Divider, Form, Input, Modal, Result, Spin, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus, faTruck } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { showNotification } from '../../../util/Notifications';
import { logUserTrack } from '../../Shared/userTrack';

const StyledModal = styled(Modal)`
  .ant-modal-close {
    color: #221f54;
  }

  .ant-modal-title {
    color: #221f54;
  }
`;

const StyledLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  margin-bottom: 15px;
  margin-top: 15px;
`;

const StyledFloatingButton = styled(Button)`
  position: static;
  z-index: 999;
  animation: 'pulse 2s infinite';
`;

const StyledSpinnerContainer = styled.div`
  height: 200px;
  margin-top: 100px;
`;

const StyledModalActions = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledSaveFee = styled.div`
  display: block;
  color: rgb(34, 31, 84);
`;

const FloatingMenuButton = ({ containerDetails }: { containerDetails: any }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [form] = Form.useForm();
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const handleSubmit = (values: any) => {
    form
      .validateFields()
      .then(() => {})
      .catch(() => {
        showNotification({
          type: 'error',
          title: 'Error',
          message: 'Please fill out all required fields',
        });
        return;
      });

    setIsFormSubmitting(true);
    const payload = {
      ...values,
      billOfLading: containerDetails.billOfLading,
      containerNumber: containerDetails.containerNumber,
      siteName: containerDetails.siteName,
      siteCode: containerDetails.siteCode,
    };
    logUserTrack('click', 'submit_arrange_transportation_form', payload);
    fetch('/api/sales/lead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...values,
        billOfLading: containerDetails.billOfLading,
        containerNumber: containerDetails.containerNumber,
        siteName: containerDetails.siteName,
        siteCode: containerDetails.siteCode,
      }),
    })
      .then((response) => {
        if (response.ok) {
          setIsConfirmed(true);
        }
      })
      .finally(() => setIsFormSubmitting(false));
  };

  const openModal = () => {
    const payload = {
      billOfLading: containerDetails.billOfLading,
      containerNumber: containerDetails.containerNumber,
      siteName: containerDetails.siteName,
      siteCode: containerDetails.siteCode,
    };
    logUserTrack('click', 'open_arrange_transportation_modal', payload);
    if (isConfirmed) return;
    setIsVisible(true);
  };

  const closeModal = () => {
    setIsVisible(false);
  };

  return (
    <>
      {!isConfirmed && (
        <Tooltip title='Schedule Post-Exam Final Delivery'>
          <StyledFloatingButton
            type='primary'
            shape='round'
            size='large'
            style={{
              animation: 'pulse 3s infinite',
            }}
            onClick={openModal}
          >
            {!isConfirmed && (
              <>
                <FontAwesomeIcon icon={faTruck} style={{ marginRight: '-5px' }} />
                <FontAwesomeIcon icon={faPlus} style={{ marginBottom: '8px' }} size={'sm'} />
                Transport
              </>
            )}
          </StyledFloatingButton>
        </Tooltip>
      )}

      <StyledModal
        title={
          <>
            {isConfirmed && <img src={'ecw_logo.png'} style={{ height: 40 }} />}
            {!isConfirmed && <>Arrange Final Delivery with ECW and Save</>}
          </>
        }
        style={{ minHeight: 500 }}
        open={isVisible}
        onCancel={() => setIsVisible(false)}
        footer={null}
      >
        <Divider style={{ margin: 0 }} />

        {!isConfirmed && (
          <StyledLogoContainer>
            <img src={'ecw_logo.png'} alt={'ECW-Logo'} />
          </StyledLogoContainer>
        )}

        {isFormSubmitting && !isConfirmed && (
          <StyledSpinnerContainer>
            <Spin tip='Loading' size='large'>
              <div className='content' />
            </Spin>
          </StyledSpinnerContainer>
        )}
        {!isFormSubmitting && isConfirmed && (
          <Result
            status='success'
            title='Success'
            subTitle='You will hear from us shortly.'
            extra={[
              <Button type={'primary'} block onClick={closeModal} style={{ backgroundColor: '#4f45c7' }}>
                Close
              </Button>,
            ]}
          />
        )}
        {!isFormSubmitting && !isConfirmed && (
          <Form form={form} layout={'vertical'} onFinish={handleSubmit}>
            <Form.Item name='name' label={'Name'} rules={[{ required: true, message: 'Please provide a valid name' }]}>
              <Input placeholder='Name' />
            </Form.Item>

            <Form.Item
              name='email'
              label={'Email'}
              rules={[{ required: true, message: 'Please provide a valid email address', type: 'email' }]}
            >
              <Input placeholder='Email' />
            </Form.Item>
            <Form.Item
              name='phoneNumber'
              label={'Phone Number'}
              rules={[
                { required: true, message: 'Please provide a Phone Number', type: 'string' },
                {
                  pattern: /^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                  message: 'Please enter a valid US Phone Number',
                },
              ]}
            >
              <Input placeholder='Phone Number' />
            </Form.Item>
            <Form.Item
              name='zipCode'
              label={'Destination Zip Code'}
              rules={[
                { required: true, message: 'Please provide the Destination Zip Code', type: 'string' },
                {
                  pattern: /^\d{5}(-\d{4})?$/,
                  message: 'Please enter a valid US Zip Code',
                },
              ]}
            >
              <Input placeholder='Destination Zip Code' />
            </Form.Item>
            <Form.Item name='notes' label={'Notes'}>
              <Input.TextArea placeholder='Company, Delivery Details...' />
            </Form.Item>

            <StyledModalActions>
              <StyledSaveFee>
                Save $125 Chassis Flip Fee Per Container
                <br />
                <span style={{ color: '#e89797' }}>Excludes Reefers</span>
              </StyledSaveFee>

              <div>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button style={{ background: '#C62038' }} type='primary' htmlType='submit'>
                    Submit
                    <FontAwesomeIcon icon={faCheck} style={{ marginLeft: 10 }} />
                  </Button>
                </Form.Item>
              </div>
            </StyledModalActions>
          </Form>
        )}
      </StyledModal>
    </>
  );
};

export default FloatingMenuButton;
